// Neue Plak MUST load first so the content area matches the live page (EDS only
// references the family; a standalone Valkyrie app does not get the faces for
// free). Then the extracted EDS utilities + the ported app styles (global CSS is
// only allowed via this single import chain in _app).
import '../styles/neue-plak.css';
import '../styles/eds.scss';
import '../styles/covid.scss';
// be_grotesk Founders Grotesk QA preview override (activated via ?be_grotesk=true).
import '@eventbrite/webapp-fonts/fonts.css';

import React from 'react';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { FavIconLinks } from '@eventbrite/webapp-public-content';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { getTldLocaleFromRequest } from '@eventbrite/webapp-locales/dist/request';
import { getServerUrl, withTrustedHostHeaders } from '../lib/serverSideProps';
import { formatLocale, getLocaleMessages } from '../../i18n/locale';
import { setGettextMessages } from '../lib/i18n';
import { BspProvider } from '@eventbrite/bsp-sdk/react';
import { useBspConfig } from '../lib/bsp-config';

// One client for the whole app. The consumer header fetches the signed-in user
// client-side via react-query, so a provider is required or it crashes with
// "No QueryClient set".
const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: false, refetchOnWindowFocus: false },
    },
});

type Covid19AppProps = AppProps & {
    stage: Stage;
    requestLocale?: string;
    messages?: Record<string, string>;
};

// NOTE: eb-ui's `index.jsx` bootstrapped Google Analytics (site-analytics
// initDimensionsFromState/trackInitialPageView) — GA is deprecated platform-wide.
// The eb-ui app never fired any custom event beyond that automatic pageview hit
// (the `analytics` import in Covid19Page.jsx was dead code), so restoring
// "average eb-ui app" parity only requires the automatic-pageview equivalent:
// PicoX's `page_view`, auto-fired by <BspProvider> on mount, zero extra wiring
// needed. Consent continues to flow through `ConsentManagerScripts`
// (@eventbrite/webapp-data); BspProvider's own PicoX event processor checks
// window.airgap per-event, so page_view (and any future manual track() calls)
// respect the same Transcend consent gate GA/Heap used.
//
// Caveat: like Heap before it, PicoX undercounts vs. raw traffic for
// regulated-regime visitors who decline Analytics consent — use CloudFront/
// CloudWatch/API Gateway metrics as the source of truth for total visit volume,
// and treat this page_view as the behavioural signal on the consented subset.
//
// <ConsentManagerScripts /> (which renders TranscendConsentScript) is placed
// as a sibling BEFORE <BspProvider>, per the bsp-sdk README's documented
// ordering — not required for correctness (consent is re-evaluated
// synchronously per-event with a fail-open default if airgap isn't ready
// yet), but matches the convention used by other consumers.
//
// `stage` MUST come through App.getInitialProps (below), not a module-level
// `process.env.STAGE` read: Next.js only inlines env vars into the client
// bundle when explicitly configured to (e.g. NEXT_PUBLIC_*) — a bare
// `process.env.STAGE` is `undefined` in the browser, so a module-level read
// silently falls back to Stage.TEST on every client (soft-nav) render and
// mislabels PROD page_view traffic as PicoX 'staging' (Bugbot PR #89, HIGH).
// getInitialProps runs server-side on first load and Next serializes its
// return value into pageProps for the client, so this is the one place the
// real server-injected STAGE safely reaches the browser.
export default function App({ Component, pageProps, stage, requestLocale, messages }: Covid19AppProps) {
    const bspConfig = useBspConfig(stage);
    // Hydrate BEFORE first render so gettext call-sites resolve translated
    // strings on both server and client (messages serialize into __NEXT_DATA__).
    setGettextMessages(messages);

    return (
        <QueryClientProvider client={queryClient}>
            {/* Catalog hydrated in getInitialProps (TLD-derived locale via the
                parent-chain merge); untranslated keys render source text. The
                chrome consumes react-intl; page copy goes through the
                catalog-aware gettext shim. */}
            <IntlProvider locale={formatLocale(requestLocale)} defaultLocale="en-US" messages={messages || {}}>
                <Head>
                    <FavIconLinks stage={stage} />
                </Head>
                <ConsentManagerScripts />
                <BspProvider config={bspConfig}>
                    <Component {...pageProps} />
                </BspProvider>
            </IntlProvider>
        </QueryClientProvider>
    );
}

App.getInitialProps = async (context: AppContext) => {
    const stage = stageFromString(process.env.STAGE || Stage.TEST);
    const appProps = await NextApp.getInitialProps(context);
    // TLD-derived locale from the TRUSTED host (same sanitization the gSSP
    // applies via withTrustedHostHeaders — the raw request Host in prod is the
    // internal apigw origin). The page Varies on host, so the CDN caches
    // per-TLD and localized SSR is safe.
    const req = context.ctx.req;
    const requestLocale = req
        ? getTldLocaleFromRequest(
              withTrustedHostHeaders(
                  { ...context.ctx, req },
                  getServerUrl(req.headers as unknown as Record<string, string | string[] | undefined>),
              ).req,
          )
        : undefined;
    const messages = await getLocaleMessages(requestLocale);
    return { ...appProps, stage, requestLocale, messages };
};
